import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  buildPersonaOptions,
  PersonaOption,
} from "../../components/PersonaSelectorSheet";
import { PersonaCarouselSheet } from "../../components/PersonaCarouselSheet";
import { getUserDisplayName } from "../../utils/index";
import { getUserStorageKey } from "../../utils/storage";
import { toCanonicalPersonaId } from "../../utils/persona";
import { useAppSettings } from "../../components/providers/AppSettingsProvider";
import { useVoiceConfig } from "../../contexts/VoiceConfigContext";
import {
  SupportedLanguage,
  getProviderByLanguage,
} from "../../services/realtimeVoice";
import {
  PERSONA_OPTION_TEMPLATES,
} from "../../config/voicePersonaConfig";
import type { VoiceGender } from "../../config/voicePersonaConfig";
import { checkActiveMembership } from "../../utils/subscriptionUtils";
import { verifyJWTToken, loadAuthUser, getJWTToken } from "../../services/auth";
import { logEvent } from "../../services/analytics";
import { getPageBackgroundGradient } from "../../config/theme";
import { useWhisperImmersive } from "../../contexts/WhisperImmersiveContext";

// ... imports

// Hooks
import { useWhisperEngine } from "./hooks/useWhisperEngine";
import { useConversation } from "./hooks/useConversation";

// Components
import { Header } from "./components/Header";
import { Transcript } from "./components/Transcript";
import { MicButton } from "./components/MicButton";
import { TypeChatComposer } from "./components/TypeChatComposer";
import { BackgroundLayer } from "./components/BackgroundLayer";
import { FirstTimeLoading } from "./components/FirstTimeLoading";
import { ConversationList } from "./components/ConversationList";
import { SuggestionBar } from "./components/SuggestionBar";
import { getFallbackHints } from "./constants/fallbackHints";
import { WhisperTutorialOverlay, TutorialStep } from "./components/WhisperTutorialOverlay";
import { useKeyboardBottomInset } from "../../hooks/useKeyboardBottomInset";

// Sheets
import { CelebrationSheet } from "./components/Sheets/CelebrationSheet";
import { EmotionRecordSheet } from "./components/Sheets/EmotionRecordSheet";
import { EmotionSelectorSheet } from "./components/Sheets/EmotionSelectorSheet";
import { EmojiInputSheet } from "./components/Sheets/EmojiInputSheet";
import { DurationLimitModal } from "./components/Sheets/DurationLimitModal";

export default function WhisperPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, theme, language: appLanguage } = useAppSettings();
  const isDark = theme === "dark";
  const { mappings, refreshConfig } = useVoiceConfig();

  // Local Settings State
  const [selectedPersonaId, setSelectedPersonaId] = useState<string>(() => {
    try {
      const user = loadAuthUser();
      // 1. 优先：上次使用的角色（聊天中切换后持久化）
      const lastKey = getUserStorageKey("t2s_persona_style", user?.id);
      const last = localStorage.getItem(lastKey);
      if (last) return toCanonicalPersonaId(last);
      // 2. 其次：onboarding 时选择的角色
      const obKey = getUserStorageKey("onboarding-data", user?.id);
      const raw = localStorage.getItem(obKey);
      if (raw) {
        const data = JSON.parse(raw);
        if (data?.voice) return toCanonicalPersonaId(data.voice);
      }
    } catch {}
    return "listener";
  });
  const [selectedGender, setSelectedGender] = useState<string>(() => {
    try {
      const user = loadAuthUser();
      const key = getUserStorageKey("t2s_voice_gender", user?.id);
      const saved = localStorage.getItem(key);
      if (saved === "male" || saved === "female") return saved;
    } catch {}
    return "female";
  });

  // Identify language
  // We explicitly type language as SupportedLanguage.
  // Assuming appLanguage maps correctly or we need a mapper.
  // For now casting appLanguage to SupportedLanguage if compatible, or default en-US.
  // In original code: mapUILanguageToVoiceLanguage was used.
  const language = (
    appLanguage?.startsWith("zh")
      ? "zh"
      : appLanguage?.startsWith("es")
        ? "es"
        : "en"
  ) as SupportedLanguage;

  // UI States
  const [firstTimeLoading, setFirstTimeLoading] = useState(() => {
    return !sessionStorage.getItem("t2s_has_shown_loading");
  });
  const [showLiveCaptions, setShowLiveCaptions] = useState(true);
  /** 底部导航「Type chat」：仅键盘输入、不采麦克风 */
  const [textChatSession, setTextChatSession] = useState(false);

  // User Status & Name
  const [isPremium, setIsPremium] = useState(false);
  const [userName, setUserName] = useState(() => {
    const user = loadAuthUser();
    if (!user) return t('profile.guest.nickname'); // Guest: "Hi, Guest"
    const authName =
      user?.nickname ||
      (user?.full_name
        ? user.full_name.split(" ")[0]
        : user?.email
          ? user.email.split("@")[0]
          : undefined);
    return getUserDisplayName(authName);
  });
  const [hasAddonBalance, setHasAddonBalance] = useState(false);

  // Check Status & User
  const checkPremium = useCallback(async () => {
    // 1. Update name immediately from local data (prevents "User" placeholder delay)
    const localUser = loadAuthUser();
    if (!localUser) {
      setUserName(t('profile.guest.nickname')); // Guest
    } else {
      const localAuthName =
        localUser?.nickname ||
        (localUser?.full_name
          ? localUser.full_name.split(" ")[0]
          : localUser?.email
            ? localUser.email.split("@")[0]
            : undefined);
      setUserName(getUserDisplayName(localAuthName));
    }

    const active = await checkActiveMembership();
    await verifyJWTToken();
    const user = loadAuthUser();

    // user.full_name or user.email or 'User'
    // Use getUserDisplayName to prioritize Onboarding name.
    // Also prefer nickname from auth user if available.
    const authName =
      user?.nickname ||
      (user?.full_name
        ? user.full_name.split(" ")[0]
        : user?.email
          ? user.email.split("@")[0]
          : undefined);
    if (user) setUserName(getUserDisplayName(authName)); // skip for guests

    const isWhitelisted = user?.is_whitelisted === true;

    // 2. Set initial premium state from cached membership + whitelist
    setIsPremium(active || isWhitelisted);

    // 服务端资料依赖 JWT；访客或无 token 时不请求（否则会 Not authenticated 刷控制台）
    const jwt = getJWTToken();
    if (jwt) {
      try {
        const { getUserProfile } = await import("../../services/userService");
        const profile = await getUserProfile();

        // Sync back to localStorage (The Source of Truth)
        const currentUser = loadAuthUser();
        if (currentUser) {
          const { saveAuthUser } = await import("../../services/auth");
          saveAuthUser({
            ...currentUser,
            nickname: profile.nickname ?? undefined,
            full_name: profile.full_name ?? undefined,
            is_whitelisted: profile.is_whitelisted,
            has_completed_tutorial: profile.has_completed_tutorial,
          });
        }

        // Update UI state with DB value to ensure consistency if local was stale
        const dbName =
          profile.nickname ||
          (profile.full_name
            ? profile.full_name.split(" ")[0]
            : profile.email
              ? profile.email.split("@")[0]
              : "User");
        if (loadAuthUser()) setUserName(getUserDisplayName(dbName)); // skip for guests

        setHasAddonBalance(profile.addon_balance_seconds > 0);
        setIsPremium(active || profile.is_whitelisted);

        // Tutorial: If marked complete in DB but not in localStorage, sync it
        if (profile.has_completed_tutorial) {
          const user = loadAuthUser();
          const key = getUserStorageKey("whisper-tutorial-v1", user?.id);
          if (localStorage.getItem(key) !== "done") {
            localStorage.setItem(key, "done");
            setTutorialStep(0);
          }
        }
      } catch (e) {
        console.error("Failed to check addon balance / profile sync", e);
        // Fallback to cached results if server profile fetch fails
        setIsPremium(active || isWhitelisted);
      }
    }
  }, []);

  // Sheets
  const [showPersonaSheet, setShowPersonaSheet] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showEmotionRecord, setShowEmotionRecord] = useState(false); // Full screen
  const [showEmotionSelector, setShowEmotionSelector] = useState(false); // Partial/Top sheet (Subsequent conversations)

  // Emotion Selector Sheet (Bottom sheet for active conversation) - wait, this was the confusing part.
  // The "EmotionSelectorSheet" COMPONENT is actually the "Top Slide-in".
  // "activeConversationId" is not needed for sheet state, but expanded state is.
  const [isEmotionSheetExpanded, setIsEmotionSheetExpanded] = useState(false);
  // Emoji Input
  const [showEmojiInput, setShowEmojiInput] = useState(false);
  const [customEmoji, setCustomEmoji] = useState("");
  const [isEmojiInputSheet, setIsEmojiInputSheet] = useState(false);

  // Limit Modal
  const [showDurationLimitModal, setShowDurationLimitModal] = useState(false);
  // Journal generation loading overlay
  const [generatingJournal, setGeneratingJournal] = useState(false);
  const [journalSkipped, setJournalSkipped] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  // --- First-Time Tutorial ---
  const [tutorialStep, setTutorialStep] = useState<TutorialStep>(() => {
    try {
      const user = loadAuthUser();
      if (!user || user?.has_completed_tutorial) return 0; // Guests skip tutorial
      const key = getUserStorageKey("whisper-tutorial-v1", user?.id);
      const saved = localStorage.getItem(key);
      if (saved === "done") return 0;
      if (saved === "step2") return 2;
      if (saved === "step3") return 3;
      if (saved === "awaiting") return 0; // handled by ref
      return 1; // new user: start at step 1
    } catch {
      return 0;
    }
  });
  // Tracks when the user dismissed step 1 manually before starting a conversation
  const tutorialAwaitingConversationRef = useRef<boolean>(
    (() => {
      try {
        const user = loadAuthUser();
        const key = getUserStorageKey("whisper-tutorial-v1", user?.id);
        return localStorage.getItem(key) === "awaiting";
      } catch {
        return false;
      }
    })()
  );

  const handleTutorialAdvance = () => {
    const user = loadAuthUser();
    const key = user ? getUserStorageKey("whisper-tutorial-v1", user?.id) : null;

    setTutorialStep((prev) => {
      if (prev === 1) {
        // Step 1 dismissed manually: hide tooltip and wait for conversation to start
        tutorialAwaitingConversationRef.current = true;
        if (key) localStorage.setItem(key, "awaiting");
        return 0;
      }
      const next = (prev + 1) as TutorialStep;
      if (next > 3) {
        // Mark tutorial as done
        if (key) localStorage.setItem(key, "done");
        
        // Sync to Backend
        import("../../services/userService").then(({ updateUserProfile }) => {
          updateUserProfile({ has_completed_tutorial: true }).catch(err => {
             console.error("Failed to sync tutorial completion to backend", err);
          });
        });

        return 0;
      }
      
      // Save intermediate progress
      if (key) {
        if (next === 2) localStorage.setItem(key, "step2");
        if (next === 3) localStorage.setItem(key, "step3");
      }
      
      return next;
    });
  };

  // --- Proactive Interaction States ---
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inactivityTimersRef = useRef<{
    hint?: NodeJS.Timeout;
    proactive?: NodeJS.Timeout;
    exit?: NodeJS.Timeout;
  }>({});

  // Data Refs

  // --- Hooks ---

  const {
    conversations,
    currentMessages,
    activeConversationId,
    hasCompletedFirstConversation,
    conversationSelected,
    loadConversations,
    startNewConversation,
    saveConversation,
    updateCurrentMessages,
    calculateWordCount,
    selectConversation,
    setActiveConversationId,
    setConversationSelected,
    setShowTranscript,
    migrateConversationId,
    restoreConversationsFromCloud,
  } = useConversation();

  // Data Refs to track state for unmount saving (Must be before useWhisperEngine)
  const currentMessagesRef = useRef(currentMessages);
  const activeConversationIdRef = useRef(activeConversationId);
  const sessionInitialUserMsgCountRef = useRef(0);
  const hasProactivelyCheckedInRef = useRef(false);

  const {
    status,
    lastText,
    aiReplyText,
    conversationActive,
    setConversationActive,
    isConnecting,
    isUserSpeaking,
    startListening,
    stopListening,
    engine,
    getCurrentSessionDuration,
    isAISpeaking,
    isReconnecting,
    suggestions,
    setSuggestions,
  } = useWhisperEngine({
    language,
    onNewMessage: updateCurrentMessages,
    onStopConversation: () => {
      // Called when engine stops internally (e.g. error/quota)
      setConversationActive(false);
      setTextChatSession(false);
      // Do NOT call handleExit() here to avoid triggering post-chat sheets on errors
    },
    onSessionReady: (id) => {
      if (id) {
        console.log("[UI] Backend conversation_id received:", id);

        const currentId = activeConversationIdRef.current;
        if (currentId && currentId.startsWith("conv_") && id !== currentId) {
          // Migrate legacy ID to new backend UUID
          migrateConversationId(currentId, id);
        } else {
          setActiveConversationId(id);
        }
      }
    },
    onAISpeakingStateChange: (speaking) => {
      if (speaking) {
        setShowSuggestions(false);
      }
    },
    onSuggestions: (hints) => {
      if (hints && hints.length > 0) {
        setSuggestions(hints);
        setShowSuggestions(true);
      }
    },
    onDurationLimit: () => setShowDurationLimitModal(true),
    onUnmount: (buffered) => {
      console.log("[UI] useWhisperEngine unmount callback triggered", buffered);
      const msgs = [...currentMessagesRef.current];

      // Append buffered content
      if (buffered.userText) {
        msgs.push({
          id: `buffered_user_${Date.now()}`,
          role: "user",
          text: buffered.userText,
          ts: Date.now(),
        });
      }
      if (buffered.aiText) {
        msgs.push({
          id: `buffered_ai_${Date.now()}`,
          role: "assistant",
          text: buffered.aiText,
          ts: Date.now(),
        });
      }

      if (msgs.length > 0) {
        console.log("[UI] Saving conversation on unmount (via callback)...");
        try {
          const user = loadAuthUser();
          const storageKey = getUserStorageKey("t2s_conversations", user?.id);
          const raw = localStorage.getItem(storageKey);
          const conversations = raw ? JSON.parse(raw) : [];
          const activeId = activeConversationIdRef.current;
          const duration = getCurrentSessionDuration();

          let updated = [...conversations];

          if (activeId) {
            // Update existing
            let found = false;
            updated = updated.map((conv: any) => {
              if (conv.id === activeId) {
                found = true;
                const wordCount = calculateWordCount(msgs);
                const totalDuration = (conv.duration || 0) + duration;
                const firstMsg = msgs.find((m: any) => m.role === "user")?.text || msgs.find((m: any) => m.role === "assistant")?.text || "";
                const displayTitle = firstMsg.length > 30 ? firstMsg.slice(0, 30) + "..." : firstMsg;
                const isGeneric = !conv.title || conv.title === "New Conversation";
                const hasUserMsg = msgs.some((m: any) => m.role === "user");

                return {
                  ...conv,
                  messages: msgs,
                  updatedAt: Date.now(),
                  title: (isGeneric || hasUserMsg) ? (displayTitle || conv.title) : conv.title,
                  personaId: selectedPersonaId || conv.personaId,
                  gender: selectedGender || conv.gender,
                  wordCount,
                  duration: totalDuration,
                };
              }
              return conv;
            });

            // UPSERT: If activeId provided but not found in LS
            if (!found) {
              const now = Date.now();
              const firstMsg = msgs.find((m: any) => m.role === "user")?.text || msgs.find((m: any) => m.role === "assistant")?.text || "";
              const title = firstMsg.length > 30 ? firstMsg.slice(0, 30) + "..." : firstMsg;
              const wordCount = calculateWordCount(msgs);

              const newConv = {
                id: activeId,
                messages: msgs,
                createdAt: now,
                updatedAt: now,
                title: title || "New Conversation",
                language: language,
                personaId: selectedPersonaId,
                gender: selectedGender,
                wordCount,
                duration: duration,
              };
              updated = [newConv, ...updated];
            }
          } else {
            // Create new
            const now = Date.now();
            const firstMsg = msgs.find((m: any) => m.role === "user")?.text || msgs.find((m: any) => m.role === "assistant")?.text || "";
            const title = firstMsg.length > 30 ? firstMsg.slice(0, 30) + "..." : firstMsg;
            const wordCount = calculateWordCount(msgs);

            const newConv = {
              id: `conv_${now}`,
              messages: msgs,
              createdAt: now,
              updatedAt: now,
              title: title || "New Conversation",
              language: language,
              personaId: selectedPersonaId,
              gender: selectedGender,
              wordCount,
              duration: duration,
            };
            updated = [newConv, ...updated];
          }

          localStorage.setItem(storageKey, JSON.stringify(updated));
        } catch (e) {
          console.error("Failed to save conversation on unmount", e);
        }
      }
    },
  });

  const { setImmersive: setWhisperLayoutImmersive } = useWhisperImmersive();
  const keyboardBottomInset = useKeyboardBottomInset();

  // 全屏沉浸：开始语音后为 true；点停止不退出沉浸，仅左上角退出回到主页时关闭（见 handleExit）
  useEffect(() => {
    if (conversationActive) {
      setWhisperLayoutImmersive(true);
    }
  }, [conversationActive, setWhisperLayoutImmersive]);

  useEffect(() => {
    return () => {
      setWhisperLayoutImmersive(false);
      setTextChatSession(false);
    };
  }, [setWhisperLayoutImmersive]);

  // Tutorial: advance to step 2 when conversation starts
  // Handles both: user dismissed step 1 early (awaiting ref) or step 1 still showing
  useEffect(() => {
    if (conversationActive && (tutorialStep === 1 || tutorialAwaitingConversationRef.current)) {
      tutorialAwaitingConversationRef.current = false;
      setTutorialStep(2);
    }
  }, [conversationActive, tutorialStep]);

  // --- Timer Logic ---
  useEffect(() => {
    const clearTimers = () => {
      Object.values(inactivityTimersRef.current).forEach(
        (t) => t && clearTimeout(t),
      );
      inactivityTimersRef.current = {};
    };

    // 文字聊天模式下：isAISpeaking / isUserSpeaking 永远为 false（无音频 / 无麦克风），
    // 不能依赖它们来判断"是否静默"，直接跳过所有倒计时避免误退出。
    if (
      !conversationActive ||
      isUserSpeaking ||
      isAISpeaking ||
      isConnecting ||
      isReconnecting ||
      textChatSession
    ) {
      clearTimers();
      if (isUserSpeaking) {
        hasProactivelyCheckedInRef.current = false;
      }
      if (isUserSpeaking || isAISpeaking) {
        setShowSuggestions(false);
      }
      return;
    }

    // 1. Start 2s Timer - Hints
    inactivityTimersRef.current.hint = setTimeout(() => {
      // If Azure Pipeline, prioritize dynamic suggestions from LLM
      if (getProviderByLanguage(language) === "azure_pipeline") {
        engine?.log?.(
          "[Proactive] Requesting dynamic suggestions at 2s...",
          "info",
        );
        // We actually need a command for this. Backend implemented 'get_suggestions'
        engine?.triggerCommand?.("get_suggestions", {
          persona_id: selectedPersonaId,
        });
        // We don't set fallback here to avoid flicker.
        // The onSuggestions callback will handle setSuggestions and show.
      } else {
        // For other providers, use Fallback hints immediately
        const lastTurn = currentMessages[currentMessages.length - 1];
        const fallback = getFallbackHints(
          selectedPersonaId,
          language,
          lastTurn?.text,
        );
        setSuggestions(fallback);
        setShowSuggestions(true);
      }
    }, 2000);

    // 2. Start 10s Timer - Proactive Query
    inactivityTimersRef.current.proactive = setTimeout(() => {
      if (hasProactivelyCheckedInRef.current) return;

      console.log("[Proactive] Triggering 10s proactive query");
      hasProactivelyCheckedInRef.current = true;
      engine?.triggerCommand?.("trigger_chat_text_query", {
        content: `[System Instruction: User has been silent for 10 seconds. Based on the Persona: '${selectedPersonaId}' and the current conversation context, please proactively say one short sentence to care for the user or keep the conversation going. Requirements: 1. Extremely short. 2. Highly relevant to the conversation history, avoid being generic. 3. If there is no specific topic to follow up on, just ask: 'Still wanna chat?' (Please respond in the conversation's language: ${language})]`,
        source: "system",
      });
    }, 10000);

    // 3. Start 20s Timer - Auto Exit (仅在语音模式下启用)
    inactivityTimersRef.current.exit = setTimeout(() => {
      console.log("[Proactive] 20s total silence. Auto-exiting (silent)...");
      handleExit({ silent: true });
    }, 20000);

    return clearTimers;
  }, [
    conversationActive,
    isUserSpeaking,
    isAISpeaking,
    isConnecting,
    isReconnecting,
    textChatSession,
    language,
    selectedPersonaId,
    engine,
  ]);

  // --- Effects ---

  // Sync Refs for Unmount callback
  useEffect(() => {
    currentMessagesRef.current = currentMessages;
    activeConversationIdRef.current = activeConversationId;
  }, [currentMessages, activeConversationId]);

  // Cleanup Save on Unmount - REMOVED as per instructions.
  // The saving logic is now handled by the `onUnmount` callback passed to `useWhisperEngine`.

  useEffect(() => {
    checkPremium();
    const handleFocus = () => checkPremium();
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [checkPremium]);


  // Load conversations on mount
  useEffect(() => {
    loadConversations();
    // Simulate initial loading
    if (firstTimeLoading) {
      const timer = setTimeout(() => {
        setFirstTimeLoading(false);
        sessionStorage.setItem("t2s_has_shown_loading", "true");
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [loadConversations, firstTimeLoading]);

  // Cloud restore on mount if local is empty
  useEffect(() => {
    if (!firstTimeLoading && conversations.length === 0) {
      console.log(
        "[Whisper] Local history empty, attempting cloud restoration...",
      );
      restoreConversationsFromCloud(5);
    }
  }, [conversations.length, firstTimeLoading, restoreConversationsFromCloud]);

  // Hot Switch: Sync Gender to engine
  useEffect(() => {
    if (engine && selectedGender) {
      engine.setGender(selectedGender);
    }
  }, [selectedGender, engine]);

  // Hot Switch: Sync Persona ID to engine
  useEffect(() => {
    if (engine && selectedPersonaId) {
      engine.setPersonaId(selectedPersonaId);
    }
  }, [selectedPersonaId, engine]);

  // Save updated nickname to backend + local cache
  const handleSaveName = async (newName: string) => {
    try {
      const { updateUserProfile } = await import("../../services/userService");
      const { saveAuthUser } = await import("../../services/auth");
      await updateUserProfile({ nickname: newName });
      const currentUser = loadAuthUser();
      if (currentUser) saveAuthUser({ ...currentUser, nickname: newName });
      setUserName(newName);
    } catch (e) {
      console.error("[Whisper] Failed to save nickname:", e);
    }
  };

  // 持久化上次使用的角色，下次进入 Whisper 时恢复
  useEffect(() => {
    if (!selectedPersonaId) return;
    try {
      const user = loadAuthUser();
      const key = getUserStorageKey("t2s_persona_style", user?.id);
      localStorage.setItem(key, selectedPersonaId);
    } catch {}
  }, [selectedPersonaId]);

  // 持久化上次使用的性别，下次进入 Whisper 时恢复
  useEffect(() => {
    if (!selectedGender) return;
    try {
      const user = loadAuthUser();
      const key = getUserStorageKey("t2s_voice_gender", user?.id);
      localStorage.setItem(key, selectedGender);
    } catch {}
  }, [selectedGender]);

  // Hot Switch: JIT Refresh when opening Persona Sheet
  useEffect(() => {
    if (showPersonaSheet) {
      console.log("[UI] Config sheet opened, JIT refreshing config...");
      refreshConfig();
    }
  }, [showPersonaSheet, refreshConfig]);

  // Sync active conversation state for saving
  // In original code, saveConversation was called periodically or on certain events?
  // Let's assume we save on exit or explicit stop.
  // Actually, useConversation might handle auto-save if we updated it to, but it just has saveConversation function.
  // We probably want to save to localStorage periodically or on unmount.
  // Ideally, useConversation handles persistence internally on change of currentMessages.
  // (Checking useConversation content: yes, it saves to localStorage in useEffect [currentMessages])

  // Options Logic
  const personaOptions = useMemo(
    () => buildPersonaOptions(PERSONA_OPTION_TEMPLATES, language),
    [language],
  );

  // Calculate activeSystemRole
  const activeSystemRole = useMemo(() => {
    const selectedPersona = personaOptions.find(
      (p: PersonaOption) => p.id === selectedPersonaId,
    );
    return selectedPersona?.systemPrompt?.trim() || undefined;
  }, [personaOptions, selectedPersonaId]);

  // Calculate selectedToneId (Voice ID)
  const selectedToneId = useMemo(() => {
    // Fallback logic similar to handleToggleListening
    if (!mappings || !mappings[selectedPersonaId]) return undefined;

    const langMap = mappings[selectedPersonaId][language];
    let voiceId = "";
    if (langMap) {
      voiceId =
        langMap[selectedGender as "male" | "female"] || langMap["female"];
    } else {
      // Fallback to English map if current language not found
      const enMap = mappings[selectedPersonaId]["en"];
      if (enMap)
        voiceId = enMap[selectedGender as "male" | "female"] || enMap["female"];
    }
    return voiceId || undefined;
  }, [mappings, selectedPersonaId, selectedGender, language]);

  // Hot Switch: Apply updates to engine immediately
  useEffect(() => {
    if (engine && activeSystemRole) {
      engine.setSystemRole(activeSystemRole);
    }
  }, [activeSystemRole, engine]);

  useEffect(() => {
    if (engine && selectedToneId) {
      engine.setSpeakerId(selectedToneId);
    }
  }, [selectedToneId, engine]);

  // Sync persona/tone to engine if changed mid-session
  useEffect(() => {
    if (conversationActive && engine) {
      console.log(
        "[Whisper] Hot-swapping persona/tone config:",
        { selectedPersonaId, selectedToneId },
      );
      engine.triggerCommand?.("update_config", {
        persona_id: selectedPersonaId,
        speaker_id: selectedToneId,
      });
    }
  }, [selectedPersonaId, selectedToneId, conversationActive, engine]);

  // Sync nickname hot-swap
  useEffect(() => {
    if (engine && userName) {
      engine.setUserName(userName);
    }
  }, [userName, engine]);

  // --- Handlers ---

  const handleExit = async (options: { silent?: boolean } = {}) => {
    if (conversationActive) {
      await stopListening();

      // Analytics: Track Voice Session End
      const duration = getCurrentSessionDuration();
      logEvent("voice_session_end", {
        duration_seconds: duration,
        message_count: currentMessages.length,
        trigger: "exit_button",
      });

      setConversationActive(false);
    }

    // Save current conversation if it has messages (even if new/no active ID)
    if (currentMessages.length > 0) {
      const duration = getCurrentSessionDuration();
      saveConversation(duration, language, selectedPersonaId, selectedGender);

      // Navigate to conversation summary page instead of showing emotion picker
      if (!options.silent) {
        // Get conversation ID — may be set from backend session or fall back to localStorage
        let conversationId =
          activeConversationId || activeConversationIdRef.current;

        if (!conversationId) {
          // For new conversations where backend ID hasn't been received yet,
          // read the most recently saved entry from localStorage
          const user = loadAuthUser();
          const convStorageKey = getUserStorageKey(
            "t2s_conversations",
            user?.id,
          );
          const raw = localStorage.getItem(convStorageKey);
          const savedConvs = raw ? JSON.parse(raw) : [];
          conversationId = savedConvs.length > 0 ? savedConvs[0].id : null;
        }

        if (conversationId) {
          // Optimized: Only wait for summary and navigate to journal if there was new user interaction.
          // Otherwise, just return to the main view immediately.
          const currentUserMsgCount = currentMessages.filter(
            (m: any) => m.role === "user",
          ).length;
          const hasNewUserInteraction =
            currentUserMsgCount > sessionInitialUserMsgCountRef.current;

          if (!hasNewUserInteraction) {
            console.log(
              "[UI] Skipping summary wait: No new user interaction in this session.",
            );
            setWhisperLayoutImmersive(false);
            setTextChatSession(false);
            setActiveConversationId(null);
            updateCurrentMessages([]);
            setConversationSelected(false);
            setShowTranscript(false);
            return;
          }

          // Show loading overlay immediately so UI doesn't appear frozen
          setGeneratingJournal(true);

          const summaryResult = await engine?.waitForSummaryResult?.(
            conversationId,
            12000,
          );
          if (summaryResult) {
            console.log("[UI] Summary terminal result before navigation", {
              conversationId,
              status: summaryResult.status,
              reason: summaryResult.reason,
              meaningful: summaryResult.meaningful,
              meaningReason: summaryResult.meaning_reason,
            });
          }

          const skippedAsNotMeaningful =
            summaryResult?.status === "skipped" &&
            (summaryResult?.reason === "not_meaningful" ||
              summaryResult?.meaningful === false ||
              summaryResult?.meaning_reason === "no_user_text");

          if (skippedAsNotMeaningful) {
            console.log(
              "[journal] summary navigation skipped by meaningfulness check",
              {
                conversationId,
                skippedReason:
                  summaryResult?.meaning_reason || summaryResult?.reason,
              },
            );
            // Show a friendly hint in the overlay, then auto-dismiss after 2.5s
            setJournalSkipped(true);
            await new Promise((resolve) => setTimeout(resolve, 2500));
            setGeneratingJournal(false);
            setJournalSkipped(false);
            setWhisperLayoutImmersive(false);
            setTextChatSession(false);
            setActiveConversationId(null);
            updateCurrentMessages([]);
            setConversationSelected(false);
            setShowTranscript(false);
            return;
          }

          const user = loadAuthUser();
          const convStorageKey = getUserStorageKey(
            "t2s_conversations",
            user?.id,
          );
          const raw = localStorage.getItem(convStorageKey);
          const savedConvs = raw ? JSON.parse(raw) : [];
          const savedConv =
            savedConvs.find((c: { id: string }) => c.id === conversationId) ||
            savedConvs[0];

          const today = new Date();
          const dateKey = today.toISOString().split("T")[0];

          setGeneratingJournal(false);
          navigate("/journal-detail", {
            state: {
              dateKey,
              dateLabel: "Today",
              conversations: savedConv ? [savedConv] : [],
              emotions: [],
              conversationId,
            },
          });
          return;
        }
      }
    }

    // --- COMMENTED OUT: Emotion picker flow (replaced by summary page navigation above) ---
    // let shouldShowSheet = false;
    // const wordCount = calculateWordCount(currentMessages);
    // if (wordCount > 10 && !options.silent) {
    //     shouldShowSheet = true;
    // }
    //
    // if (shouldShowSheet && !options.silent && !showCelebration && !showEmotionRecord && !showEmotionSelector) {
    //     const user = loadAuthUser();
    //     const CELEBRATION_SHOWN_KEY = getUserStorageKey('t2s_celebration_shown_date', user?.id);
    //     const today = new Date().toDateString();
    //     const lastShownDate = localStorage.getItem(CELEBRATION_SHOWN_KEY);
    //
    //     if (lastShownDate !== today) {
    //         setShowCelebration(true);
    //         localStorage.setItem(CELEBRATION_SHOWN_KEY, today);
    //     } else {
    //         setShowEmotionSelector(true);
    //     }
    //     return;
    // }
    // --- END COMMENTED OUT ---

    // Reset conversation state to return to conversation list (main interface)
    setWhisperLayoutImmersive(false);
    setTextChatSession(false);
    setActiveConversationId(null);
    updateCurrentMessages([]);
    setConversationSelected(false);
    setShowTranscript(false);
    // Note: We don't navigate away, just reset state to show the conversation list
  };

  const handleSelectEmotion = (emotionId: string, isSheet: boolean) => {
    if (emotionId === "express") {
      setIsEmojiInputSheet(isSheet);
      setShowEmojiInput(true);
      if (isSheet) {
        // Keep selector open? Or wait?
        // Original: setShowEmotionSheet(false) IF isSheet?
        // No, original: if 'express', show input.
        // handleCustomEmojiSubmit closes all.
      } else {
        setShowEmotionRecord(false);
      }
      return;
    }

    // Save logic
    const emotionMap: Record<string, { emoji: string; label: string }> = {
      happy: { emoji: "😊", label: t("emotion.happy") },
      calm: { emoji: "😌", label: t("emotion.calm") },
      beloved: { emoji: "🥰", label: t("emotion.beloved") },
      tired: { emoji: "😩", label: t("emotion.tired") },
      angry: { emoji: "😠", label: t("emotion.angry") },
      sad: { emoji: "😥", label: t("emotion.sad") },
      excited: { emoji: "🤩", label: t("emotion.excited") },
      frustrated: { emoji: "😞", label: t("emotion.frustrated") },
      express: { emoji: "➕", label: t("emotion.express") },
    };

    const emotion = emotionMap[emotionId] || emotionMap.calm;

    const conversationId =
      activeConversationId || activeConversationIdRef.current;
    console.log("[UI] Saving Emotion Record, ID:", conversationId);

    const record = {
      emotion: emotionId,
      emoji: emotion.emoji,
      label: emotion.label,
      reflection: "",
      timestamp: Date.now(),
      conversationId,
    };

    try {
      const user = loadAuthUser();
      const storageKey = getUserStorageKey("t2s_emotion_records", user?.id);
      const existing = localStorage.getItem(storageKey);
      const records = existing ? JSON.parse(existing) : [];
      records.push(record);
      localStorage.setItem(storageKey, JSON.stringify(records));
      // toast.success(t('emotion.recorded'));
    } catch (e) {
      console.error(e);
    }

    if (isSheet) {
      // Top/Partial Sheet
      setShowEmotionSelector(false);
      setIsEmotionSheetExpanded(false);
      navigate("/journals");
    } else {
      // Full Record Sheet
      setShowEmotionRecord(false);
      navigate("/journals");
    }
  };

  const handleCustomEmojiSubmit = (emoji: string) => {
    setShowEmojiInput(false);
    setCustomEmoji("");

    if (!emoji || emoji.trim() === "") return;

    const trimmed = emoji.trim().slice(0, 2);
    const record = {
      emotion: "custom",
      emoji: trimmed,
      label: t("emotion.express"),
      reflection: "",
      timestamp: Date.now(),
      conversationId: activeConversationId || activeConversationIdRef.current,
    };

    try {
      const user = loadAuthUser();
      const storageKey = getUserStorageKey("t2s_emotion_records", user?.id);
      const existing = localStorage.getItem(storageKey);
      const records = existing ? JSON.parse(existing) : [];
      records.push(record);
      localStorage.setItem(storageKey, JSON.stringify(records));
      // toast.success(t('emotion.recorded'));
    } catch (e) {
      console.error(e);
    }

    if (isEmojiInputSheet) {
      setShowEmotionSelector(false);
      setIsEmotionSheetExpanded(false);
      navigate("/journals");
    } else {
      setShowEmotionRecord(false);
      navigate("/journals");
    }
  };

  // Check Guest View
  useEffect(() => {
    const user = loadAuthUser();
    if (!user) {
      logEvent("guest_whisper_view");
    }
  }, []); // Run once on mount

  const beginListeningSession = useCallback(
    async (opts?: { textOnly?: boolean }) => {
      logEvent("voice_session_start", {
        total_conversations: conversations.length,
        input_mode: opts?.textOnly ? "text" : "voice",
      });

      setShowCelebration(false);
      setShowEmotionRecord(false);
      setShowEmotionSelector(false);
      setShowEmojiInput(false);

      if (!opts?.textOnly) {
        setTextChatSession(false);
      }

      const isNewConversation = !conversationSelected;

      if (isNewConversation) {
        startNewConversation();
        sessionInitialUserMsgCountRef.current = 0;
      } else {
        sessionInitialUserMsgCountRef.current = currentMessages.filter(
          (m) => m.role === "user",
        ).length;
      }

      await startListening(
        selectedPersonaId,
        selectedToneId,
        selectedGender,
        activeSystemRole,
        {
          intent: isNewConversation ? "new" : "resume",
          conversationId: activeConversationId || undefined,
          textOnly: opts?.textOnly === true,
        },
      );

      if (opts?.textOnly) {
        setTextChatSession(true);
      }
    },
    [
      activeConversationId,
      activeSystemRole,
      conversationSelected,
      conversations.length,
      currentMessages,
      selectedGender,
      selectedPersonaId,
      selectedToneId,
      startListening,
      startNewConversation,
    ],
  );

  const routeAutoStart = location.state as
    | { autoStartVoice?: boolean; autoStartTextChat?: boolean }
    | undefined;
  const autoStartVoice = Boolean(routeAutoStart?.autoStartVoice);
  const autoStartTextChat = Boolean(routeAutoStart?.autoStartTextChat);

  useEffect(() => {
    if (
      (!autoStartVoice && !autoStartTextChat) ||
      firstTimeLoading ||
      generatingJournal
    ) {
      return;
    }

    navigate(location.pathname, { replace: true, state: {} });

    void (async () => {
      if (isConnecting) return;
      if (!loadAuthUser()) {
        setShowLoginModal(true);
        return;
      }
      if (conversationActive) return;
      try {
        if (autoStartTextChat) {
          await beginListeningSession({ textOnly: true });
        } else {
          await beginListeningSession();
        }
      } catch (e) {
        console.error("[Whisper] autoStart (voice/text) failed", e);
      }
    })();
  }, [
    autoStartVoice,
    autoStartTextChat,
    beginListeningSession,
    conversationActive,
    firstTimeLoading,
    generatingJournal,
    isConnecting,
    location.pathname,
    navigate,
  ]);

  // Check if user is logged in
  const handleToggleListening = async () => {
    if (isConnecting) return;

    // Check if user is logged in
    const user = loadAuthUser();
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    if (conversationActive) {
      // Stop
      await stopListening();
      const duration = getCurrentSessionDuration();

      // Analytics: Track Voice Session End
      logEvent("voice_session_end", {
        duration_seconds: duration,
        message_count: currentMessages.length,
        trigger: "toggle_button",
      });

      saveConversation(duration, language, selectedPersonaId, selectedGender);
      setConversationActive(false);
      setTextChatSession(false);
    } else {
      await beginListeningSession();
    }
  };

  const handleContinueConversation = (id: string) => {
    const restored = selectConversation(id);
    // 恢复该对话保存的 persona 和 gender，优先于全局最后使用记录
    if (restored?.personaId) setSelectedPersonaId(restored.personaId);
    if (restored?.gender) setSelectedGender(restored.gender);

    // Track initial user messages to potentially skip summary on exit if nothing new happens
    const conv = conversations.find((c) => c.id === id);
    sessionInitialUserMsgCountRef.current =
      conv?.messages?.filter((m) => m.role === "user").length || 0;
  };

  const applyPersonaAndGender = (personaId: string, gender: VoiceGender) => {
    setSelectedPersonaId(toCanonicalPersonaId(personaId));
    setSelectedGender(gender);
    setShowPersonaSheet(false);
  };

  const handleSelectSuggestion = (text: string) => {
    setShowSuggestions(false);
    engine?.triggerCommand?.("trigger_chat_text_query", {
      content: text,
      source: "suggestion",
    });
  };

  const handleSendTypeChat = (text: string) => {
    setShowSuggestions(false);
    engine?.triggerCommand?.("trigger_chat_text_query", {
      content: text,
      source: "text_input",
    });
  };

  // --- Render ---

  if (firstTimeLoading) {
    return <FirstTimeLoading isDark={isDark} t={t} />;
  }

  // Journal generation loading overlay
  if (generatingJournal) {
    return (
      <div
        className={`fixed inset-0 flex flex-col items-center justify-center bg-gradient-to-b ${getPageBackgroundGradient(theme)}`}
      >
        {journalSkipped ? (
          // Friendly skip message
          <>
            <div className="mb-6">
              <span className="text-5xl">✨</span>
            </div>
            <p className="text-lg font-semibold mb-3 text-white text-center px-8">
              {t("whisper.journalSkipped.title")}
            </p>
            <p className="text-sm text-center px-10 text-white/60 leading-relaxed">
              {t("whisper.journalSkipped.subtitle")}
            </p>
          </>
        ) : (
          // Generating state
          <>
            {/* Animated moon glow */}
            <div className="relative mb-8">
              <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center animate-pulse">
                <div className="w-16 h-16 rounded-full bg-white/15 flex items-center justify-center">
                  <span className="text-4xl">✨</span>
                </div>
              </div>
              {/* Orbiting dot */}
              <div
                className="absolute inset-0 flex items-center justify-center"
                style={{ animation: "spin 2s linear infinite" }}
              >
                <div style={{ transform: "translateY(-52px)" }}>
                  <div className="w-2.5 h-2.5 rounded-full bg-white/70" />
                </div>
              </div>
            </div>
            <p className="text-lg font-semibold mb-2 text-white">
              {t("whisper.generatingJournal")}
            </p>
            <p className="text-sm text-center px-8 text-white/50">
              {t("whisper.generatingJournalSubtitle")}
            </p>
            {/* Progress dots */}
            <div className="flex gap-1.5 mt-6">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-1.5 h-1.5 rounded-full bg-white/60"
                  style={{
                    animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      className={`fixed inset-0 overflow-hidden ${isDark ? "bg-gray-900" : "bg-gray-50"}`}
    >
      <BackgroundLayer conversationActive={conversationActive} />

      <Header
        language={language}
        showLiveCaptions={showLiveCaptions}
        setShowLiveCaptions={setShowLiveCaptions}
        isPremium={isPremium}
        hasAddonBalance={hasAddonBalance}
        selectedPersonaId={selectedPersonaId}
        onExit={handleExit}
        onShowPersonaSheet={() => setShowPersonaSheet(true)}
        showExitAndLanguage={
          conversationSelected || conversationActive || !!activeConversationId
        }
      />

      {/* Main Content Area */}
      {!conversationSelected && !conversationActive && !activeConversationId ? (
        // If no active conversation, show list (Card Scroll)
        <ConversationList
          conversations={conversations}
          greetingMessage={{ name: userName, greeting: t("whisper.welcome") }}
          onContinue={handleContinueConversation}
          onSaveName={loadAuthUser() ? handleSaveName : undefined}
        />
      ) : (
        // Active Transcript
        <Transcript
          currentMessages={currentMessages}
          lastText={lastText}
          aiReplyText={aiReplyText}
          isUserSpeaking={isUserSpeaking}
          status={status}
          conversationActive={conversationActive}
          showLiveCaptions={showLiveCaptions}
          messagesBottomPaddingClass={
            textChatSession && conversationActive ? "pb-44" : "pb-28"
          }
        />
      )}

      {/* Suggestions Bar - Positioned above mic / type composer */}
      <div
        className="absolute left-0 right-0 z-40 flex justify-center pointer-events-none"
        style={{
          bottom:
            (textChatSession && conversationActive ? 200 : 340) +
            keyboardBottomInset,
        }}
      >
        <div className="pointer-events-auto">
          <SuggestionBar
            suggestions={suggestions}
            visible={
              showSuggestions &&
              conversationActive &&
              !isUserSpeaking &&
              !isAISpeaking
            }
            onSelect={handleSelectSuggestion}
          />
        </div>
      </div>

      {!(textChatSession && conversationActive) && (
        <MicButton
          conversationActive={conversationActive}
          isConnecting={isConnecting}
          isReconnecting={isReconnecting}
          status={status}
          hasCompletedFirstConversation={hasCompletedFirstConversation}
          onClick={handleToggleListening}
        />
      )}

      {textChatSession && conversationActive && (
        <TypeChatComposer
          onSend={handleSendTypeChat}
          disabled={isConnecting || isReconnecting}
          keyboardBottomInset={keyboardBottomInset}
        />
      )}

      {/* Sheets & Modals */}

      <PersonaCarouselSheet
        show={showPersonaSheet}
        onClose={() => setShowPersonaSheet(false)}
        selectedPersonaId={selectedPersonaId}
        selectedGender={selectedGender as VoiceGender}
        onSelect={applyPersonaAndGender}
        options={personaOptions}
        title={t("whisper.persona.carouselTitle") || "Choose Lumi's Vibe"}
        maleLabel={t("common.gender.male") || "Male"}
        femaleLabel={t("common.gender.female") || "Female"}
        selectLabel={t("whisper.persona.select") || "Select"}
      />

      {/* COMMENTED OUT: Emotion picker flow (replaced by summary page navigation) */}
      {false && (
        <>
          <CelebrationSheet
            show={showCelebration}
            totalConversationCount={conversations.length}
            onClose={() => {
              setShowCelebration(false);
              setShowEmotionRecord(true);
            }}
          />
          <EmotionRecordSheet
            show={showEmotionRecord}
            onSelectEmotion={(id, isSheet) => handleSelectEmotion(id, isSheet)}
          />
          {/* Partial / Top Sheet for Subsequent Conversations */}
          <EmotionSelectorSheet
            show={showEmotionSelector}
            expanded={isEmotionSheetExpanded}
            onExpandChange={setIsEmotionSheetExpanded}
            onClose={() => {
              setShowEmotionSelector(false);
              navigate(-1);
            }}
            onSelectEmotion={handleSelectEmotion}
          />
          <EmojiInputSheet
            show={showEmojiInput}
            value={customEmoji}
            isSheetMode={isEmojiInputSheet}
            onChange={setCustomEmoji}
            onSubmit={handleCustomEmojiSubmit}
            onClose={() => setShowEmojiInput(false)}
          />
        </>
      )}

      <DurationLimitModal
        show={showDurationLimitModal}
        actionText={t("purchase.upgrade") || "Upgrade Plan"}
        onAction={() => {
          setShowDurationLimitModal(false);
          navigate("/purchase");
        }}
        onClose={() => setShowDurationLimitModal(false)}
      />

      <DurationLimitModal
        show={showLoginModal}
        title={t("auth.loginRequired") || "Login Required"}
        description=""
        actionText={t("auth.signIn") || "Log In"}
        onAction={() => {
          setShowLoginModal(false);
          navigate("/splash", { replace: true, state: { forceLogin: true } });
        }}
        onClose={() => setShowLoginModal(false)}
      />

      <DurationLimitModal
        show={isReconnecting}
        title={t("whisper.reconnect.title")}
        description={t("whisper.reconnect.desc")}
        hideAction={true}
        onClose={() => {
          // Manual stop if user closes the modal (e.g. by clicking backdrop)
          handleToggleListening();
        }}
      />
      <WhisperTutorialOverlay step={tutorialStep} onAdvance={handleTutorialAdvance} />
    </div>
  );
}
